import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';

import { Box, Drawer, Stack } from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { MHidden } from '../../../@material-extend';
import IconCollapse from './IconCollapse';
import NavSection from '../NavSection';
import Scrollbar from '../../../Scrollbar';
import { LogoCompany } from '../../../../assets/logos';

import useCollapseDrawer from '../../../../hooks/useCollapseDrawer';

const DRAWER_WIDTH = 230;
const COLLAPSE_WIDTH = 102;

const Sidebar = ({ isOpenSidebar, onCloseSidebar }) => {
  const theme = useTheme();
  const { pathname } = useLocation();
  const { isCollapse, collapseClick, collapseHover, onToggleCollapse, onHoverEnter, onHoverLeave } =
    useCollapseDrawer();

  useEffect(() => {
    if (isOpenSidebar) {
      onCloseSidebar();
    }
  }, [pathname]);

  const content = (
    <Stack sx={{ height: '100%', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header fixe — bouton collapse + logo société */}
      <Stack sx={styleHeader(isCollapse)}>
        <MHidden width="lgDown">
          <Stack direction="row" justifyContent="flex-end" sx={{ width: '100%', py: 0.25 }}>
            <IconCollapse collapseClick={collapseClick} onToggleCollapse={onToggleCollapse} />
          </Stack>
        </MHidden>
        <Stack justifyContent="center" sx={styleLogoContainer}>
          <LogoCompany sx={styleLogoCompany(isCollapse)} />
        </Stack>
      </Stack>

      {/* Navigation — scrollable si les items dépassent */}
      <Scrollbar id="side-nav-scrollbar" sx={styleScrollbar}>
        <NavSection />
      </Scrollbar>

      {/* Logo Comète — fixe en bas */}
      <Stack sx={styleLogoCometeWrapper(isCollapse)}>
        <Box component="img" src="static/logo/comete.svg" alt="Logo Comète" draggable="false" />
      </Stack>
    </Stack>
  );

  return (
    <Stack className="side-nav" sx={styleSideNav(theme, isCollapse, collapseClick)}>
      <MHidden width="lgUp">
        <Drawer
          id="side-nav-drawer-small-screen"
          PaperProps={{ sx: { width: DRAWER_WIDTH } }}
          open={isOpenSidebar}
          onClose={onCloseSidebar}
        >
          {content}
        </Drawer>
      </MHidden>

      <MHidden width="lgDown">
        <Drawer
          id="side-nav-drawer-large-screen"
          open
          variant="persistent"
          PaperProps={{ sx: styleDrawer(theme, isCollapse, collapseHover) }}
          onMouseEnter={onHoverEnter}
          onMouseLeave={onHoverLeave}
        >
          {content}
        </Drawer>
      </MHidden>
    </Stack>
  );
};

const styleSideNav = (theme, isCollapse, collapseClick) => ({
  ...(collapseClick && { position: 'absolute' }),
  [theme.breakpoints.up('lg')]: {
    flexShrink: 0,
    width: isCollapse ? COLLAPSE_WIDTH : DRAWER_WIDTH,
    transition: theme.transitions.create('width', {
      duration: theme.transitions.duration.complex
    })
  }
});

const styleDrawer = (theme, isCollapse, collapseHover) => ({
  width: DRAWER_WIDTH,
  borderRight: `1px solid ${theme.palette.grey[300]}`,
  bgcolor: theme.palette.background.paper,
  ...(isCollapse && {
    width: COLLAPSE_WIDTH
  }),
  ...(collapseHover && {
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: theme.customShadows.z20,
    bgcolor: alpha(theme.palette.background.default, 0.88)
  })
});

const styleScrollbar = {
  display: 'flex',
  flexFlow: 'column nowrap',
  flex: 1,
  minHeight: 0,
  '& .simplebar-content': {
    minHeight: '100%',
    display: 'flex',
    flexDirection: 'column',
    px: 1,
    py: 0.75
  }
};

const styleHeader = (isCollapse) => ({
  flexFlow: 'column nowrap',
  alignItems: isCollapse ? 'stretch' : 'center',
  justifyContent: 'center',
  gap: 3,
  p: 1
});

const styleLogoContainer = {
  alignItems: 'center'
};

const styleLogoCompany = (isCollapse) => ({
  height: isCollapse ? 64 : 160,
  width: isCollapse ? 64 : 160,
  my: 2
});

const styleLogoCometeWrapper = (isCollapse) => ({
  flexShrink: 0,
  alignItems: isCollapse ? 'center' : 'flex-start',
  justifyContent: 'flex-end',
  p: 2,
  filter: 'grayscale(100%)',
  opacity: 0.2,
  img: {
    height: 'auto',
    maxWidth: isCollapse ? 64 : 100
  }
});

Sidebar.propTypes = {
  isOpenSidebar: PropTypes.bool,
  onCloseSidebar: PropTypes.func
};

export default Sidebar;
